"use client"
import { useState } from "react"
import { motion } from "framer-motion"
import {
  MapPin,
  Search,
  Heart,
  Users,
  Shield,
  Star,
  ChefHat,
  Truck,
  Sparkles,
  Target,
  Globe,
  Award,
  Zap,
} from "lucide-react"
import Header from "../components/Header"
import { Link } from "react-router-dom"
import Footer from "../components/Footer"
import { useSelector } from "react-redux"

export const HomeFoodDelivery = () => {
  const pageBnrImg = {
    position: "relative" ,
    backgroundImage: `url('./media/frontend/img/about/blog/1920x700/banner-3.jpg')`,
    backgroundPosition: "center",
    backgroundSize: "cover",
  }

  const { userInfo } = useSelector((state) => state.user)
  const [zipCode, setZipCode] = useState("")
  const [hoveredCity, setHoveredCity] = useState(null)
  const [hoveredZip, setHoveredZip] = useState(null)

  const cities = [
    "Karachi",
    "Islamabad",
    "Lahore",
    "Multan",
    "Peshawar",
    "Swat",
    "Mardan",
    "Quetta",
    "Hyderabad",
    "Faisalabad",
    "Rawalpindi",
    "Gujranwala",
    "Sialkot",
    "Bahawalpur",
    "Muzaffarabad",
    "Gilgit",
    "Skardu",
    "Abbottabad",
    "Hunza"
  ]

const zipCodes = [
  "74000", // Karachi
  "44000", // Islamabad
  "54000", // Lahore
  "60000", // Multan
  "25000", // Peshawar
  "19200", // Swat (Mingora)
  "23200", // Mardan
  "87300", // Quetta
  "71000", // Hyderabad
  "38000", // Faisalabad
  "46000", // Rawalpindi
  "52250", // Gujranwala
  "51310", // Sialkot
  "63100", // Bahawalpur
  "13100", // Muzaffarabad
  "15100", // Gilgit
  "16100", // Skardu
  "22010", // Abbottabad
  "15750"  // Hunza (Aliabad area)
]

  const features = [
    {
      title: "Authentic Homemade Meals",
      description: "Enjoy meals crafted with love and care by talented home shefs.",
      icon: Heart,
      color: "from-red-500 to-pink-600",
      bgColor: "from-red-50 to-pink-50",
    },
    {
      title: "Variety and Personalization",
      description: "Discover a range of cuisines and meal options tailored to your preferences.",
      icon: Star,
      color: "from-yellow-500 to-orange-600",
      bgColor: "from-yellow-50 to-orange-50",
    },
    {
      title: "Fresh and Hygienic",
      description: "All meals are prepared with fresh ingredients and adhere to the highest hygiene standards.",
      icon: Shield,
      color: "from-green-500 to-emerald-600",
      bgColor: "from-green-50 to-emerald-50",
    },
    {
      title: "Convenient Delivery",
      description: "Get your favorite meals delivered quickly and reliably to your doorstep.",
      icon: Truck,
      color: "from-blue-500 to-indigo-600",
      bgColor: "from-blue-50 to-indigo-50",
    },
  ]

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const fadeInLeft = {
    initial: { opacity: 0, x: -80 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const fadeInRight = {
    initial: { opacity: 0, x: 80 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99] },
  }

  const scaleIn = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.6, ease: "easeOut" },
  }

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const cardHover = {
    scale: 1.05,
    y: -10,
    transition: {
      duration: 0.3,
      ease: [0.6, -0.05, 0.01, 0.99],
    },
  }

  const floatingAnimation = {
    y: [-10, 10, -10],
    transition: {
      duration: 4,
      repeat: Number.POSITIVE_INFINITY,
      ease: "easeInOut",
    },
  }

  return (
    <>
      <Header />

      {/* Enhanced Hero Section */}
      <section className="lg:px-2 relative overflow-hidden">
        <div className="pageBanr py-32 relative" style={pageBnrImg}>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/70"></div>

          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="absolute top-20 left-10 w-3 h-3 bg-primary rounded-full"
              animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
            />
            <motion.div
              className="absolute top-40 right-20 w-2 h-2 bg-white rounded-full"
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, delay: 1 }}
            />
            <motion.div
              className="absolute bottom-32 left-1/4 w-4 h-4 bg-primary/60 rounded-full"
              animate={{ scale: [1, 2, 1], opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2.5, repeat: Number.POSITIVE_INFINITY, delay: 0.5 }}
            />
          </div>

          <motion.div
            className="container mx-auto relative z-[1] text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <div className="text-center lg:px-0 px-4">
              <motion.div
                className="inline-block mb-8"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.8, delay: 0.5, type: "spring" }}
              >
                <div className="w-24 h-24 bg-gradient-to-br from-primary/20 to-primary/40 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20">
                  <MapPin className="w-12 h-12 text-primary" />
                </div>
              </motion.div>

              <motion.h1
                className="font-bold text-5xl sm:text-6xl md:text-7xl md:leading-none leading-snug uppercase text-white tracking-wider mb-8 bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Where Does Shefs Deliver?
              </motion.h1>

              <motion.div
                className="w-32 h-1 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mb-8"
                initial={{ width: 0 }}
                animate={{ width: 128 }}
                transition={{ duration: 1, delay: 0.8 }}
              />

              <motion.h3
                className="text-4xl text-white/90 mb-12 font-light italic"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 1 }}
              >
                Food delivery
              </motion.h3>

              <motion.div
                className="lg:w-2/5 sm:w-1/2 w-full mx-auto"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.2 }}
              >
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Enhanced Content Section */}
        <div className="relative">
          <div className="container mx-auto py-24">
            <motion.div
              className="text-center mb-16"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <motion.div className="inline-flex items-center space-x-3 mb-6" variants={scaleIn}>
                <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                  <ChefHat className="w-6 h-6 text-primary" />
                </div>
                <span className="text-primary font-semibold text-lg uppercase tracking-wider">Our Service</span>
              </motion.div>

              <h2 className="font-bold text-4xl lg:text-5xl uppercase tracking-wider mb-6">
                Where Shefs Cooks &{" "}
                <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  Delivers
                </span>{" "}
                Homemade Food!
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent mx-auto mb-8" />
            </motion.div>

            <motion.div
              className="mt-16 max-w-6xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <div className="grid md:grid-cols-2 gap-8">
                <motion.div
                  className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50"
                  variants={fadeInLeft}
                  whileHover={cardHover}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Heart className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      You can get delicious homemade food delivered right to your door with Shefs Cloud's Home Food
                      Delivery services. Bringing together passionate home shefs and food lovers, we serve high-quality,
                      freshly prepared meals.
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50"
                  variants={fadeInRight}
                  whileHover={cardHover}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Our mission is to bring culinary innovation to the forefront of technology at Shefs Cloud. We at
                      Shefs Cloud are committed to empowering shefs, culinary businesses, as well as food enthusiasts
                      through the provision of better home food solutions tailored to meet the individual needs of
                      shefs, culinary businesses, and food enthusiasts.
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50"
                  variants={fadeInLeft}
                  whileHover={cardHover}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Zap className="w-6 h-6 text-primary" />
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      There is no doubt that seamless, efficient, and intelligent digital solutions will play a critical
                      role in the future of the food industry. We have designed our platform in such a way that it
                      simplifies operations, increases productivity, and enables businesses to focus on what truly
                      matters: creating exceptional culinary experiences for their guests.
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50"
                  variants={fadeInRight}
                  whileHover={cardHover}
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div className="text-lg text-gray-700 leading-relaxed">
  <p className="mb-4">
    Shefs Cloud empowers passionate home cooks to share their culinary creations with a wider audience. Our platform makes it easy to:
  </p>
  <ul className="space-y-2">
    <li className="flex items-center space-x-2">
      <Star className="w-4 h-4 text-primary" />
      <span>Showcase your talent and unique recipes.</span>
    </li>
    <li className="flex items-center space-x-2">
      <Star className="w-4 h-4 text-primary" />
      <span>Reach food enthusiasts looking for homemade goodness.</span>
    </li>
    <li className="flex items-center space-x-2">
      <Star className="w-4 h-4 text-primary" />
      <span>Manage orders efficiently with our intuitive tools.</span>
    </li>
  </ul>
</div>

                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Enhanced Cities and Zip Codes Section */}
            {/* <motion.div
              className="border-t border-gray-200 mt-24 pt-16"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            > */}
              {/* <div className="mb-16">
                <motion.div className="text-center mb-12" variants={fadeInUp}>
                  <div className="inline-flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                      <Globe className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-primary font-semibold text-lg uppercase tracking-wider">Coverage Area</span>
                  </div>
                  <h2 className="text-3xl lg:text-4xl font-bold mb-4 uppercase">
                    Top{" "}
                    <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                      Cities
                    </span>
                  </h2>
                  <div className="w-20 h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent mx-auto" />
                </motion.div>

                <motion.div
                  className="grid lg:grid-cols-5 md:grid-cols-3 grid-cols-2 gap-4"
                  variants={staggerContainer}
                >
                  {cities.map((city, index) => (
                    <motion.div
                      key={index}
                      className="group relative"
                      variants={fadeInUp}
                      onHoverStart={() => setHoveredCity(index)}
                      onHoverEnd={() => setHoveredCity(null)}
                    >
                      <motion.div
                        className="text-center bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                        whileHover={cardHover}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                          <MapPin className="w-6 h-6 text-primary mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
                          <h6 className="text-lg font-bold text-gray-800 group-hover:text-primary transition-colors duration-300">
                            {city}
                          </h6>
                        </div>
                      </motion.div>
                    </motion.div>
                  ))}
                </motion.div>
              </div> */}

              {/*<div>
                <motion.div className="text-center mb-12" variants={fadeInUp}>
                  <div className="inline-flex items-center space-x-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                      <Search className="w-6 h-6 text-primary" />
                    </div>
                    <span className="text-primary font-semibold text-lg uppercase tracking-wider">Service Areas</span>
                  </div>
                  <h2 className="text-3xl lg:text-4xl font-bold mb-4 uppercase">
                    Top{" "}
                    <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                      Zip Codes
                    </span>
                  </h2>
                  <div className="w-20 h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent mx-auto" />
                </motion.div>

                <motion.div
                  className="grid lg:grid-cols-5 md:grid-cols-3 grid-cols-2 gap-4"
                  variants={staggerContainer}
                >
                  {zipCodes.map((zip, index) => (
                    <motion.div
                      key={index}
                      className="group relative"
                      variants={fadeInUp}
                      onHoverStart={() => setHoveredZip(index)}
                      onHoverEnd={() => setHoveredZip(null)}
                    >
                      <motion.div
                        className="text-center bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                        whileHover={cardHover}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative z-10">
                          <div className="w-8 h-8 bg-gradient-to-br from-primary/10 to-primary/20 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                            <span className="text-primary font-bold text-sm">#</span>
                          </div>
                          <h6 className="text-lg font-bold text-gray-800 group-hover:text-primary transition-colors duration-300">
                            {zip}
                          </h6>
                        </div>
                      </motion.div>
                    </motion.div>
                  ))}
                </motion.div>
              </div>*/}
            {/* </motion.div> */}
          </div>
        </div>

        {/* Enhanced Features Section */}
        <div className="bg-gradient-to-br from-gray-50 via-white to-gray-50 py-24 relative overflow-hidden">
          {/* Background decorations */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-primary/3 to-transparent rounded-full blur-3xl" />
          </div>

          <div className="container mx-auto relative z-10">
            <motion.div
              className="text-center mb-20"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <motion.div className="inline-flex items-center space-x-3 mb-6" variants={scaleIn}>
                <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                  <Award className="w-6 h-6 text-primary" />
                </div>
                <span className="text-primary font-semibold text-lg uppercase tracking-wider">Why Choose Us</span>
              </motion.div>

              <h2 className="font-bold text-4xl lg:text-5xl uppercase tracking-wider mb-6">
                Why Choose{" "}
                <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  Shefs Cloud
                </span>{" "}
                Home Food Delivery?
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent mx-auto" />
            </motion.div>

            <motion.div
              className="grid lg:grid-cols-2 gap-8"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {features.map((feature, index) => {
                const IconComponent = feature.icon
                return (
                  <motion.div key={index} className="group" variants={fadeInUp}>
                    <motion.div
                      className={`relative bg-gradient-to-br ${feature.bgColor} rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 h-full border border-white/50 backdrop-blur-sm overflow-hidden`}
                      whileHover={cardHover}
                    >
                      {/* Background decoration */}
                      <div
                        className={`absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br ${feature.color} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity duration-500`}
                      />

                      <div className="relative z-10">
                        <div className="flex items-center space-x-4 mb-6">
                          <motion.div
                            className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center shadow-lg`}
                            animate={floatingAnimation}
                          >
                            <IconComponent className="w-8 h-8 text-white" />
                          </motion.div>
                        </div>

                        <h6 className="font-bold text-2xl uppercase mb-6 text-secondary group-hover:text-gray-800 transition-colors duration-300">
                          {feature.title}
                        </h6>
                        <p className="text-lg text-headGray leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                          {feature.description}
                        </p>
                      </div>
                    </motion.div>
                  </motion.div>
                )
              })}
            </motion.div>
          </div>
        </div>

        {/* Enhanced Mission Section */}
        <div className="relative">
          <div className="container mx-auto py-24">
            <motion.div
              className="text-center mb-16"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <motion.div className="inline-flex items-center space-x-3 mb-6" variants={scaleIn}>
                <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/20 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <span className="text-primary font-semibold text-lg uppercase tracking-wider">Our Purpose</span>
              </motion.div>

              <h2 className="font-bold text-4xl lg:text-5xl uppercase tracking-wider mb-6">
                Our{" "}
                <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  Mission
                </span>
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent mx-auto mb-8" />
            </motion.div>

            <motion.div
              className="max-w-4xl mx-auto"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <div className="grid md:grid-cols-1 gap-8">
                <motion.div
                  className="bg-white/80 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-white/50 text-center"
                  variants={fadeInUp}
                  whileHover={cardHover}
                >
                  <div className="flex items-center justify-center space-x-4 mb-8">
                    <motion.div
                      className="w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/20 rounded-2xl flex items-center justify-center"
                      animate={floatingAnimation}
                    >
                      <Heart className="w-8 h-8 text-primary" />
                    </motion.div>
                  </div>
                  <p className="text-xl text-gray-700 leading-relaxed mb-6">
                    Our mission is to bring people together through food, whether you're craving a nostalgic home-cooked
                    meal, exploring new flavors, or just looking for healthier alternatives.
                  </p>
                  <p className="text-xl text-gray-700 leading-relaxed font-semibold">
                    You can get delicious, homemade food right now. Experience the joy of home-cooked meals anytime,
                    anywhere!
                  </p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  )
}

export default HomeFoodDelivery
